import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { ArrowRight, CheckCircle } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "Does Sorvaine support Arabic-language RFPs?", answer: "Sorvaine is optimised for English-language tenders today. Bilingual Arabic/English RFPs can be processed by uploading the English sections — full Arabic NLP support is on the roadmap." },
  { question: "Is Sorvaine suitable for ADNOC and government RFPs?", answer: "Yes. Sorvaine handles the structural complexity of ADNOC, Etihad, DEWA, ADDC, and federal government tenders — including multi-volume submissions, technical and commercial separation, and ICV requirements." },
  { question: "Can Sorvaine help with In-Country Value (ICV) responses?", answer: "Yes. Sorvaine extracts ICV scoring criteria from the RFP, maps them to dedicated response sections, and helps you structure evidence around procurement, employment, and investment commitments." },
];

export default function Uae() {
  return (
    <ContentPageLayout
      title="RFP Response Software for UAE Bid Teams | Sorvaine"
      description="Sorvaine helps UAE bid managers respond to government and enterprise RFPs faster with AI. Built for GCC procurement standards."
      canonical={`${BASE}/uae`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "UAE", url: `${BASE}/uae` },
      ]}
      ctaHeading="Win more UAE bids — faster"
      ctaDescription="See Sorvaine on a live ADNOC, government, or enterprise RFP. Book a 20-minute demo."
    >
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Win more bids in the UAE with AI-powered proposal software
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[640px]">
        Sorvaine is built for UAE bid teams responding to ADNOC, federal government, and enterprise RFPs — with the compliance rigour GCC procurement demands.
      </p>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Built for GCC procurement</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          UAE procurement is unlike anywhere else. ADNOC's multi-volume technical/commercial submissions, federal e-tendering portals, Mubadala-affiliated entities, DEWA, ADDC, RTA, Etihad — every buyer has its own format, scoring weight, and submission gate. Add In-Country Value (ICV) scoring, Emiratisation requirements, and bilingual Arabic/English documents, and a single missed requirement can disqualify a months-long pursuit.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          Sorvaine reads the full RFP, extracts every clause — including ICV scoring criteria, mandatory certifications (ISO, ADIA, OHSAS), and bid bond requirements — and structures your response around the buyer's exact evaluation framework.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Buyers and frameworks Sorvaine handles</h2>
        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
          {[
            "ADNOC (HQ, Onshore, Offshore, Sour Gas, LNG)",
            "Federal Government (FAHR, MoF, MoCCAE)",
            "Mubadala-affiliated entities",
            "DEWA, ADDC, FEWA, EWEC",
            "RTA, Etihad Rail, Department of Transport",
            "ADQ portfolio companies",
            "EGA, Emirates Steel, Borouge",
            "Banking and financial services RFPs",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-white/50">
              <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Why UAE bid teams choose Sorvaine</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Most bid managers we speak to in the UAE are managing 3-5 live pursuits across multiple operating companies, each with its own template, portal, and pre-qualification track. The bottleneck is never capability — it's the manual work of mapping the same company capability story to ten different RFP structures, gathering ICV evidence from procurement, and chasing technical leads for input.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          Sorvaine compresses that work. Upload the RFP — including thick ADNOC pricing volumes — and Sorvaine generates the technical section outline, maps the ICV criteria to dedicated response slots, and drafts initial content from your previously submitted (and won) bids.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">In-Country Value (ICV) ready</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          ICV is the UAE's single biggest scoring lever on competitive bids. Sorvaine identifies the specific ICV scoring model used in each RFP — supplier-tier scoring, employment, manufacturing investment, training spend — and structures your response so evaluators can verify each commitment without hunting through appendices.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          For Emiratisation commitments, Sorvaine flags the buyer's stated thresholds and prompts your team to evidence specific role plans rather than generic statements.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Get started</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Whether you're an EPC contractor chasing ADNOC packages, a consultancy bidding for federal work, or an SME breaking into government procurement, Sorvaine adapts to how your team already works. <Link to="/signup" className="text-primary hover:underline">Get started</Link> or <Link to="/book-demo" className="text-primary hover:underline">book a demo</Link> on one of your live RFPs.
        </p>
        <div className="mt-6">
          <Link to="/signup">
            <button className="inline-flex items-center justify-center px-8 h-11 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97]">
              Get started
              <ArrowRight className="ml-2 w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">FAQ</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>
    </ContentPageLayout>
  );
}
